import { z } from "zod";
import { ArticleType } from "@/types/enums";

const i18nStringSchema = z.object({
  ko: z.string().min(1, "한국어 텍스트를 입력해주세요"),
  en: z.string().nullable(),
});

const authorSNSSchema = z.object({
  platformName: z.string().min(1, "플랫폼 이름을 입력해주세요"),
  link: i18nStringSchema,
});

// Coerces null/empty I18n to undefined for optional fields.
// Type assertion needed: z.preprocess erases input type to unknown,
// breaking react-hook-form Resolver inference.
const optionalI18nSchema = z.preprocess(
  (val) => {
    if (!val || typeof val !== "object") return undefined;
    if (!(val as Record<string, unknown>).ko) return undefined;
    return val;
  },
  i18nStringSchema.optional()
) as unknown as z.ZodOptional<typeof i18nStringSchema>;

const authorInfoSchema = z.object({
  name: i18nStringSchema,
  profileImageURL: optionalI18nSchema,
  socialAccounts: z.preprocess(
    (val) => {
      if (!val || !Array.isArray(val) || val.length === 0) return undefined;
      return val;
    },
    z.array(authorSNSSchema).optional()
  ) as unknown as z.ZodOptional<z.ZodArray<typeof authorSNSSchema>>,
  biography: optionalI18nSchema,
});

const thumbnailSchema = z.object({
  url: i18nStringSchema,
  alt: i18nStringSchema,
});

const baseArticleSchema = z.object({
  title: i18nStringSchema,
  excerpt: i18nStringSchema,
  thumbnail: z.preprocess(
    (val) => {
      if (!val || typeof val !== "object") return undefined;
      const obj = val as Record<string, Record<string, unknown> | undefined>;
      if (!obj.url?.ko && !obj.alt?.ko) return undefined;
      return val;
    },
    thumbnailSchema.optional()
  ) as unknown as z.ZodOptional<typeof thumbnailSchema>,
  authorInfo: authorInfoSchema,
});

export const blogArticleSchema = baseArticleSchema.extend({
  type: z.literal(ArticleType.BLOG),
  url: i18nStringSchema.refine(
    (val) => /^https?:\/\/.+/.test(val.ko),
    { message: "올바른 URL을 입력해주세요 (https://...)", path: ["ko"] }
  ),
});

export const premiumArticleMetaSchema = baseArticleSchema.extend({
  thumbnail: thumbnailSchema,
  freeContentHtml: z.string().min(1, "무료 콘텐츠 구간을 설정해주세요"),
});

export const premiumArticlePublishSchema = premiumArticleMetaSchema.extend({
  fileId: z.string().min(1, "파일을 선택해주세요"),
  publishNow: z.boolean().optional(),
});

export const articleMetaFormSchema = baseArticleSchema.extend({
  thumbnail: thumbnailSchema,
});

export const premiumArticleEditSchema = z.object({
  excerpt: i18nStringSchema,
  thumbnail: thumbnailSchema,
  authorInfo: authorInfoSchema,
  freeContentHtml: z.string().min(1, "무료 콘텐츠 구간을 설정해주세요"),
});

export type BlogArticleFormValues = z.infer<typeof blogArticleSchema>;
export type PremiumArticlePublishFormValues = z.infer<typeof premiumArticlePublishSchema>;
export type PremiumArticleMetaFormValues = z.infer<typeof premiumArticleMetaSchema>;
export type PremiumArticleEditFormValues = z.infer<typeof premiumArticleEditSchema>;
export type ArticleMetaFormValues = z.infer<typeof articleMetaFormSchema>;
